import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import {
  FulfillServiceOrderStockRequestItemPayload,
  ServiceOrderStockRequestDetail,
  ServiceOrderStockRequestHistoryDetail,
  ServiceOrderStockRequestItemDetail,
  UpdateServiceOrderStockRequestStatusPayload,
  StockMovementsService
} from '../../../services/stock-movements.service';
import { ErrorService } from '../../../shared/services/error.service';
import { Item } from '../../../models/Item';
import { ItemService } from '../../../services/item.service';

interface RequestItemPortalState {
  searchTerm: string;
  stockItemsAll: Item[];
  stockSearchResults: Item[];
  hasLoadedStockItems: boolean;
  loadingStockItems: boolean;
  selectedStockItem: Item | null;
  fulfillmentType: 'Total' | 'Parcial';
  quantity: number;
}

@Component({
  selector: 'app-stock-request-details',
  templateUrl: './stock-request-details.component.html',
  styleUrls: ['./stock-request-details.component.css']
})
export class StockRequestDetailsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  /** Valores enviados no PUT de status; alinhar ao que o backend aceita. */
  private readonly statusEmSeparacao = 'Em Separação';
  private readonly statusCancelado = 'Cancelado';
  private readonly statusAtendido = 'Atendido';

  requestId: number | null = null;
  isLoading = false;
  isSubmittingBatch = false;
  isUpdatingPedidoStatus = false;
  error: string | null = null;
  requestDetail: ServiceOrderStockRequestDetail | null = null;
  portalStates: Record<number, RequestItemPortalState> = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private stockMovementsService: StockMovementsService,
    private errorService: ErrorService,
    private itemService: ItemService
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    this.requestId = idParam ? Number(idParam) : null;

    if (!this.requestId) {
      this.error = 'Solicitação inválida.';
      return;
    }

    this.loadRequestDetail(this.requestId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack(): void {
    this.router.navigate(['/stock/requests']);
  }

  loadRequestDetail(requestId: number): void {
    this.isLoading = true;
    this.error = null;

    this.stockMovementsService.getServiceOrderStockRequestById(requestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          this.requestDetail = detail;
          this.isLoading = false;
          // [DEBUG ATENDIMENTO] remover após validar
          console.log('[atendimento-os] detail recebido:', {
            status: detail?.status,
            isEmSeparacao: this.isPedidoEmSeparacao(),
            itens: (detail?.items || []).map(i => ({
              id: i.serviceOrderStockRequestItemId,
              status: i.status,
              requestedStockId: i.requestedStockId,
              requestedQuantity: i.requestedQuantity,
              fulfilledQuantity: i.fulfilledQuantity
            }))
          });
          if (this.isPedidoEmSeparacao()) {
            this.initializePortalStates();
          } else {
            this.portalStates = {};
          }
        },
        error: () => {
          this.error = 'Erro ao carregar detalhes da solicitação.';
          this.isLoading = false;
        }
      });
  }

  /** Status do pedido (não do item de linha). */
  isPedidoEmSeparacao(): boolean {
    return this.isStatusEmSeparacao(this.requestDetail?.status);
  }

  isPedidoCancelado(): boolean {
    const s = this.normalizeRequestStatus(this.requestDetail?.status);
    return s === 'cancelado' || s === 'cancelled';
  }

  isPedidoAtendido(): boolean {
    const s = this.normalizeRequestStatus(this.requestDetail?.status);
    return (
      s === 'atendido' ||
      s === 'fulfilled' ||
      s === 'requestfulfilled'
    );
  }

  /** Pedido aguardando retirada (após confirmar atendimento dos itens, antes de marcar como Atendido). */
  isPedidoAguardandoRetirada(): boolean {
    const s = this.normalizeRequestStatus(this.requestDetail?.status);
    if (!s) {
      return false;
    }
    return (
      s.includes('aguardando retirada') ||
      s.includes('aguardando_retirada') ||
      s.includes('aguardandoretirada') ||
      s.includes('awaitingpickup') ||
      s.includes('awaiting pickup') ||
      s.includes('awaiting_pickup') ||
      s.includes('readyforpickup') ||
      s.includes('ready for pickup')
    );
  }

  /** Inicia atendimento: pedido vai para Em Separação (PUT). */
  canAtenderPedido(): boolean {
    if (!this.requestDetail) {
      return false;
    }
    if (
      this.isPedidoCancelado() ||
      this.isPedidoAtendido() ||
      this.isPedidoEmSeparacao() ||
      this.isPedidoAguardandoRetirada()
    ) {
      return false;
    }
    return true;
  }

  /** Confirmação final: pedido em "Aguardando Retirada" vira "Atendido" (PUT). */
  canConfirmarAtendimentoPedido(): boolean {
    if (!this.requestDetail) {
      return false;
    }
    if (this.isPedidoCancelado() || this.isPedidoAtendido()) {
      return false;
    }
    return this.isPedidoAguardandoRetirada();
  }

  /** Rejeição = pedido cancelado (PUT). */
  canRejeitarPedido(): boolean {
    if (!this.requestDetail) {
      return false;
    }
    if (this.isPedidoCancelado() || this.isPedidoAtendido()) {
      return false;
    }
    return true;
  }

  atenderPedido(): void {
    if (!this.requestDetail?.serviceOrderStockRequestId || !this.canAtenderPedido()) {
      return;
    }
    this.putPedidoStatus(
      this.statusEmSeparacao,
      'Pedido em separação para atendimento de itens'
    );
  }

  rejeitarPedido(): void {
    if (!this.requestDetail?.serviceOrderStockRequestId || !this.canRejeitarPedido()) {
      return;
    }
    const ok = confirm(
      'Deseja rejeitar este pedido? O status será alterado para Cancelado.'
    );
    if (!ok) {
      return;
    }
    this.putPedidoStatus(
      this.statusCancelado,
      'Pedido rejeitado e cancelado pelo usuário'
    );
  }

  /**
   * Marca o pedido como Atendido a partir de "Aguardando Retirada".
   * PUT {{stockBase}}/service-order-stock-requests/{id}/status
   * Body: { status: "Atendido", description: "Acompanhamento manual no painel" }
   */
  confirmarAtendimentoPedido(): void {
    if (!this.requestDetail?.serviceOrderStockRequestId || !this.canConfirmarAtendimentoPedido()) {
      return;
    }
    const ok = confirm(
      'Confirmar atendimento do pedido? O status será alterado para Atendido.'
    );
    if (!ok) {
      return;
    }
    this.putPedidoStatus(
      this.statusAtendido,
      'Acompanhamento manual no painel'
    );
  }

  private putPedidoStatus(status: string, description: string): void {
    const id = this.requestDetail?.serviceOrderStockRequestId;
    if (!id) {
      return;
    }
    const payload: UpdateServiceOrderStockRequestStatusPayload = {
      status,
      description
    };
    this.isUpdatingPedidoStatus = true;
    this.stockMovementsService
      .updateServiceOrderStockRequestStatus(id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isUpdatingPedidoStatus = false;
          this.errorService.showInfoMessage('Status do pedido atualizado.');
          if (this.requestId) {
            this.loadRequestDetail(this.requestId);
          }
        },
        error: () => {
          this.isUpdatingPedidoStatus = false;
        }
      });
  }

  private normalizeRequestStatus(status: string | undefined): string {
    return (status || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  canFulfillItem(item: ServiceOrderStockRequestItemDetail): boolean {
    if (this.getRemainingQuantity(item) <= 0) {
      return false;
    }
    const s = this.normalizeRequestStatus(item.status);
    // Status vazio: considera elegível (defensivo: ítem novo sem status definido).
    if (!s) {
      return true;
    }
    // Status proibidos: já atendido, cancelado, rejeitado.
    const blocked = new Set([
      'fulfilled',
      'requestfulfilled',
      'atendido',
      'cancelado',
      'cancelled',
      'rejected',
      'rejeitado'
    ]);
    if (blocked.has(s)) {
      return false;
    }
    // Demais status são tratados como “em aberto para atendimento”
    // (pending, requested, pendente, solicitado, em separacao, partiallyfulfilled, atendido parcialmente, etc.).
    return true;
  }

  getPortalState(requestItemId: number): RequestItemPortalState {
    if (!this.portalStates[requestItemId]) {
      this.portalStates[requestItemId] = this.createPortalState();
    }
    return this.portalStates[requestItemId];
  }

  onStockSearchTermChange(item: ServiceOrderStockRequestItemDetail, searchTerm: string): void {
    const state = this.getPortalState(item.serviceOrderStockRequestItemId);
    state.searchTerm = searchTerm;
    this.applyStockSearchFilter(state);
  }

  refreshStockItemsList(item: ServiceOrderStockRequestItemDetail): void {
    this.loadStockItemsForRequestItem(item);
  }

  selectStockItem(requestItemId: number, item: Item): void {
    const state = this.getPortalState(requestItemId);
    state.selectedStockItem = item;
  }

  onFulfillmentTypeChange(item: ServiceOrderStockRequestItemDetail, type: 'Total' | 'Parcial'): void {
    const state = this.getPortalState(item.serviceOrderStockRequestItemId);
    const remaining = this.getRemainingQuantity(item);
    state.fulfillmentType = type;
    if (type === 'Total') {
      state.quantity = remaining;
    } else {
      state.quantity = remaining > 0 ? 1 : 0;
    }
  }

  onQuantityChange(item: ServiceOrderStockRequestItemDetail, quantity: number): void {
    const state = this.getPortalState(item.serviceOrderStockRequestItemId);
    const remaining = this.getRemainingQuantity(item);
    const parsedQuantity = Number(quantity || 0);

    if (!parsedQuantity || parsedQuantity < 1) {
      state.quantity = 1;
      return;
    }

    if (parsedQuantity > remaining) {
      state.quantity = remaining;
      return;
    }

    state.quantity = parsedQuantity;
  }

  confirmBatchFulfillment(): void {
    if (!this.requestDetail) {
      return;
    }
    if (!this.isPedidoEmSeparacao()) {
      this.errorService.showErrorMessage(
        'Coloque o pedido em Em Separação (botão Atender pedido) antes de confirmar o atendimento dos itens.'
      );
      return;
    }

    const itemsToFulfill = this.buildBatchItemsPayload();
    if (itemsToFulfill.length === 0) {
      this.errorService.showErrorMessage('Selecione ao menos um item e informe uma quantidade válida para atender.');
      return;
    }

    this.isSubmittingBatch = true;

    const requestId = this.requestDetail.serviceOrderStockRequestId;
    const requests = itemsToFulfill.map((payload) =>
      this.stockMovementsService.fulfillServiceOrderStockRequestItem(requestId, payload)
    );

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSubmittingBatch = false;
          this.errorService.showInfoMessage('Atendimento enviado com sucesso.');
          if (this.requestId) {
            this.loadRequestDetail(this.requestId);
          }
        },
        error: () => {
          this.isSubmittingBatch = false;
        }
      });
  }

  private buildBatchItemsPayload(): FulfillServiceOrderStockRequestItemPayload[] {
    const payloadItems: FulfillServiceOrderStockRequestItemPayload[] = [];

    for (const item of this.requestDetail?.items || []) {
      if (!this.canFulfillItem(item)) {
        continue;
      }

      const state = this.getPortalState(item.serviceOrderStockRequestItemId);
      const selectedStockItem = state.selectedStockItem;
      const originWarehouseId = Number(item.sourceWarehouseId || 0);
      const remaining = Number(this.getRemainingQuantity(item));
      const quantity = Number(state.quantity || 0);

      if (!selectedStockItem || !originWarehouseId || quantity <= 0 || quantity > remaining) {
        continue;
      }

      payloadItems.push({
        serviceOrderStockRequestItemId: item.serviceOrderStockRequestItemId,
        itemId: Number(selectedStockItem.id),
        quantity,
        originWarehouseId,
        reason: 'Separacao para OS',
        notes: `Item ${item.serviceOrderStockRequestItemId}`
      });
    }

    return payloadItems;
  }

  private applyStockSearchFilter(state: RequestItemPortalState): void {
    const term = state.searchTerm.trim().toLowerCase();
    if (!term) {
      state.stockSearchResults = [...state.stockItemsAll];
      return;
    }

    state.stockSearchResults = state.stockItemsAll.filter((item) => {
      const itemId = String(item.id || '').toLowerCase();
      const code = (item.code || '').toLowerCase();
      const name = (item.name || '').toLowerCase();
      return itemId.includes(term) || code.includes(term) || name.includes(term);
    });
  }

  private initializePortalStates(): void {
    this.portalStates = {};
    for (const item of this.requestDetail?.items || []) {
      const state = this.createPortalState();
      state.quantity = this.getRemainingQuantity(item);
      this.portalStates[item.serviceOrderStockRequestItemId] = state;
      this.loadStockItemsForRequestItem(item);
    }
  }

  private loadStockItemsForRequestItem(item: ServiceOrderStockRequestItemDetail): void {
    const state = this.getPortalState(item.serviceOrderStockRequestItemId);
    const stockId = this.resolveStockIdFromRequestItem(item);

    if (!stockId) {
      // [DEBUG ATENDIMENTO] remover após validar
      console.warn('[atendimento-os] item sem stockId — não é possível buscar itens do estoque:', item);
      state.stockItemsAll = [];
      state.stockSearchResults = [];
      state.hasLoadedStockItems = false;
      state.loadingStockItems = false;
      return;
    }

    state.hasLoadedStockItems = true;
    state.loadingStockItems = true;

    this.itemService.getItemsByStockWithSearch(stockId, '', { page: 1, pageSize: 100 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          // [DEBUG ATENDIMENTO] remover após validar
          console.log('[atendimento-os] itens carregados do estoque', stockId, '→', result?.items?.length ?? 0);
          state.stockItemsAll = [...(result.items || [])];
          this.applyStockSearchFilter(state);
          state.loadingStockItems = false;
        },
        error: (err) => {
          // [DEBUG ATENDIMENTO] remover após validar
          console.error('[atendimento-os] erro ao buscar itens do estoque', stockId, err);
          state.loadingStockItems = false;
          state.stockItemsAll = [];
          state.stockSearchResults = [];
        }
      });
  }

  /**
   * Resolve o id do estoque a partir do item de solicitação, tolerando variações
   * de nomenclatura que o backend possa retornar (requestedStockId, stockId, etc.).
   */
  private resolveStockIdFromRequestItem(item: ServiceOrderStockRequestItemDetail): number {
    const raw = item as unknown as Record<string, unknown>;
    const candidates: unknown[] = [
      raw['requestedStockId'],
      raw['RequestedStockId'],
      raw['stockId'],
      raw['StockId']
    ];
    for (const value of candidates) {
      const num = Number(value);
      if (Number.isFinite(num) && num > 0) {
        return num;
      }
    }
    return 0;
  }

  private createPortalState(): RequestItemPortalState {
    return {
      searchTerm: '',
      stockItemsAll: [],
      stockSearchResults: [],
      hasLoadedStockItems: false,
      loadingStockItems: false,
      selectedStockItem: null,
      fulfillmentType: 'Total',
      quantity: 1
    };
  }

  private isStatusEmSeparacao(status: string | undefined): boolean {
    const s = this.normalizeRequestStatus(status);
    if (!s) {
      return false;
    }
    return (
      s.includes('em separacao') ||
      s.includes('em_separacao') ||
      s.includes('emseparacao') ||
      s.includes('inseparation') ||
      s.includes('in separation') ||
      s.includes('in_separation') ||
      s === 'separacao' ||
      s === 'separation'
    );
  }

  getRemainingQuantity(item: ServiceOrderStockRequestItemDetail): number {
    const requested = Number(item.requestedQuantity || 0);
    const fulfilled = Number(item.fulfilledQuantity || 0);
    const remaining = requested - fulfilled;
    return remaining > 0 ? remaining : 0;
  }

  translateStatus(status: string | undefined): string {
    const normalized = (status || '').toLowerCase().trim();
    const map: { [key: string]: string } = {
      requested: 'Solicitado',
      pending: 'Pendente',
      'solicitado': 'Solicitado',
      'pendente': 'Pendente',
      'em separacao': 'Em Separação',
      'aguardando compra de itens': 'Aguardando Compra de Itens',
      'aguardando retirada': 'Aguardando Retirada',
      awaitingpickup: 'Aguardando Retirada',
      readyforpickup: 'Aguardando Retirada',
      approved: 'Aprovado',
      rejected: 'Rejeitado',
      fulfilled: 'Atendido',
      partiallyfulfilled: 'Atendido Parcialmente',
      'atendido parcialmente': 'Atendido Parcialmente',
      'atendido': 'Atendido',
      'cancelado': 'Cancelado',
      cancelled: 'Cancelado',
      inprogress: 'Em Andamento'
    };
    return map[normalized] || status || '-';
  }

  translateEventType(eventType: string | undefined): string {
    const map: { [key: string]: string } = {
      RequestCreated: 'Solicitação Criada',
      ItemAdded: 'Item Adicionado',
      ItemFulfilled: 'Item Atendido',
      RequestFulfilled: 'Solicitação Atendida',
      RequestCancelled: 'Solicitação Cancelada',
      AlteracaoStatus: 'Alteração de status'
    };
    return map[eventType || ''] || eventType || '-';
  }

  formatarData(iso: string | undefined): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return String(iso);
    }
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  trackHistory(_index: number, h: ServiceOrderStockRequestHistoryDetail): number {
    return h.serviceOrderStockRequestHistoryId ?? _index;
  }
}
